import React, { useState, useEffect, useRef } from 'react';
import { bankNameList } from './data';
import CustomDropdown from './CustomDropdown';
import {
  validateBankAccountNumber,
  validateIFSCCode,
  validateRequiredField,
  checkSectionBValidity
} from './ValidationUtils';

const SectionB = ({ formData: parentFormData, onDataChange, onValidationChange }) => {
  const defaultFormData = {
    BankAccountNumber: '',
    BankName: '',
    BankBranchAddress: '',
    IFSCCode: ''
  };

  // Initialize with parentFormData if available, otherwise use default
  const initialFormData = parentFormData && parentFormData.BankAccountNumber 
    ? { ...defaultFormData, ...parentFormData }
    : defaultFormData;
  
  const [formData, setFormData] = useState(initialFormData);
  
  // Validation errors state
  const [errors, setErrors] = useState({
    BankAccountNumber: null,
    BankName: null,
    BankBranchAddress: null,
    IFSCCode: null
  });
  
  // Refs to prevent circular updates
  const lastParentDataRef = useRef(JSON.stringify(parentFormData || {}));
  const isUpdatingFromParentRef = useRef(false);

  // Update local formData when parentFormData changes (from API response)
  useEffect(() => {
    if (!parentFormData) return;
    
    const parentDataStr = JSON.stringify(parentFormData);
    
    // Only update if parent data is different and we're not currently updating from parent
    if (parentDataStr !== lastParentDataRef.current && !isUpdatingFromParentRef.current) {
      // Check if parent has new data (like from API)
      const hasNewData = parentFormData.BankAccountNumber && 
                        parentFormData.BankAccountNumber !== formData.BankAccountNumber;
      
      if (hasNewData) {
        isUpdatingFromParentRef.current = true;
        const updatedFormData = {
          ...formData,
          ...parentFormData
        };
        
        setFormData(updatedFormData);
        lastParentDataRef.current = parentDataStr;
        
        setTimeout(() => {
          isUpdatingFromParentRef.current = false;
        }, 100);
      }
    }
  }, [parentFormData]);

  // Pass data to parent component whenever it changes (but not when updating from parent)
  useEffect(() => {
    if (onDataChange && !isUpdatingFromParentRef.current) {
      onDataChange(formData);
    }
  }, [formData, onDataChange]);

  // Pass validation status to parent
  useEffect(() => {
    if (onValidationChange) {
      onValidationChange(checkSectionBValidity(formData, errors));
    }
  }, [formData, errors, onValidationChange]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));

    // Validate based on field name
    let error = null;
    switch (name) {
      case 'BankAccountNumber':
        error = validateBankAccountNumber(value);
        break;
      case 'IFSCCode':
        error = validateIFSCCode(value);
        break;
      case 'BankBranchAddress':
        error = validateRequiredField(value, 'Bank Branch Address');
        break;
      default:
        break;
    }

    // Update errors
    setErrors(prev => ({
      ...prev,
      [name]: error
    }));
  };

  const handleCustomDropdownChange = (name, value) => {
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));

    // Validate Bank Name
    let error = null;
    if (name === 'BankName') {
      error = validateRequiredField(value, 'Bank Name');
    }

    setErrors(prev => ({
      ...prev,
      [name]: error
    }));
  };

  return (
    <div className="section-container">
          <form>
            <div className="row g-3">
              {/* Bank Account Number */}
              <div className="col-md-6">
                <label htmlFor="BankAccountNumber" className="form-label">
                  Bank Account Number
                </label>
                <input
                  type="text"
                  className={`form-control ${errors.BankAccountNumber ? 'is-invalid' : ''}`}
                  id="BankAccountNumber"
                  name="BankAccountNumber"
                  value={formData.BankAccountNumber}
                  onChange={handleInputChange}
                  placeholder="Enter bank account number"
                  style={{ textTransform: 'uppercase' }}
                />
                {errors.BankAccountNumber && (
                  <div className="invalid-feedback d-block">
                    {errors.BankAccountNumber}
                  </div>
                )}
              </div>

              {/* Bank Name - Custom Searchable Dropdown */}
              <div className="col-md-6">
                <label htmlFor="BankName" className="form-label">
                  Bank Name
                </label>
                <CustomDropdown
                  options={bankNameList}
                  value={formData.BankName}
                  onChange={(event) => {
                    const value = event?.target?.value || '';
                    handleCustomDropdownChange('BankName', value);
                  }}
                  placeholder="Select or search bank name..."
                  name="BankName"
                />
                {errors.BankName && (
                  <div className="text-danger" style={{ fontSize: '0.875rem', marginTop: '0.25rem' }}>
                    {errors.BankName}
                  </div>
                )}
              </div>

              {/* Bank Branch Name & Address */}
              <div className="col-md-6">
                <label htmlFor="BankBranchAddress" className="form-label">
                  Bank Branch Name & Address
                </label>
                <input
                  type="text"
                  className={`form-control ${errors.BankBranchAddress ? 'is-invalid' : ''}`}
                  id="BankBranchAddress"
                  name="BankBranchAddress"
                  value={formData.BankBranchAddress}
                  onChange={handleInputChange}
                  placeholder="Enter bank branch name & address"
                  style={{ textTransform: 'uppercase' }}
                />
                {errors.BankBranchAddress && (
                  <div className="invalid-feedback d-block">
                    {errors.BankBranchAddress}
                  </div>
                )}
              </div>

              {/* IFSC Code */}
              <div className="col-md-6">
                <label htmlFor="IFSCCode" className="form-label">
                  IFSC Code
                </label>
                <input
                  type="text"
                  className={`form-control ${errors.IFSCCode ? 'is-invalid' : ''}`}
                  id="IFSCCode"
                  name="IFSCCode"
                  value={formData.IFSCCode}
                  onChange={handleInputChange}
                  placeholder="Enter IFSC code"
                  style={{ textTransform: 'uppercase' }}
                />
                {errors.IFSCCode && (
                  <div className="invalid-feedback d-block">
                    {errors.IFSCCode}
                  </div>
                )}
              </div>
            </div>
          </form>
    </div>
  );
};

export default SectionB;

